import {defineField, defineType} from 'sanity'
import getPageInfo, {seoGroup} from '../objects/pageInfo'
import {PageBlockTypes} from '../pageBlocks'

export default defineType({
  title: 'Fitment guide',
  name: 'fitmentGuide',
  type: 'document',
  orderings: [
    {
      title: 'Title A-Z',
      name: 'titleAsc',
      by: [{field: 'pageInfo.title', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
  ],
  fieldsets: [
    {
      name: 'fitmentData',
      title: 'Fitment data',
      options: {
        collapsible: true,
        collapsed: false,
      },
      hidden: ({ document }: { document: any }) => document?.link,
    },
  ],
  fields: [
    getPageInfo({ path: 'fitmentGuide' }),
    defineField({
      name: 'publishedAt',
      title: 'Published at',
      type: 'datetime',
      options: {
        dateFormat: 'DD.MM.YYYY',
        timeFormat: 'HH:mm',
      },
      validation: (Rule) => Rule.required(),
      initialValue: () => new Date().toISOString(),
      group: 'content',
    }),
    defineField({
      name: 'buyerGuide',
      title: 'Buyer Guide',
      type: 'reference',
      to: [{ type: 'buyerGuide' }],
      description: 'Link to the related Buyer Guide document',
      group: 'content',
    }),
    defineField({
      name: 'isEnabled',
      title: 'Enabled',
      description: 'Fitment guide needs to be enabled to be visible anywhere on the website',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'overview',
      title: 'Overview',
      description: 'Should be unique for each fitment guide',
      type: 'object',
      fields: [
        {
          name: 'title',
          title: 'Title',
          type: 'string',
          initialValue: 'Overview',
        },
        {
          title: 'Page blocks',
          name: 'pageBlocks',
          type: 'array',
          of: [...PageBlockTypes],
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'link',
      title: 'Link other guide',
      description: 'If you link other fitment guide, fitment data will be pulled from linked guide',
      type: 'boolean',
      initialValue: false,
      group: 'content',
    }),
    defineField({
      name: 'connectedFitmentGuide',
      title: 'Connected fitment guide',
      type: 'reference',
      to: [{ type: 'fitmentGuide' }],
      hidden: ({ document }: { document: any }) => !document?.link,
      options: {
        filter: ({ document }: { document: any }) => {
          // avoid referencing draft documents
          // avoid referencing documents linked to other fitment guide
          const baseQuery = `!(_id in path("drafts.**")) && !link`

          if (!document._id) {
            return {
              filter: baseQuery,
            }
          }

          const docId = document._id.replace('drafts.', '')
          const documentIDs = [docId, `drafts.${docId}`]
          // avoid self referencing
          return {
            filter: `${baseQuery} && !(_id in $documentIDs)`,
            params: {
              documentIDs,
            },
          }
        },
      },
      group: 'content',
    }),
    defineField({
      title: 'Fitments',
      name: 'fitments',
      fieldset: 'fitmentData',
      type: 'array',
      validation: (Rule) =>
        Rule.custom((fitments: any, context: any) => {
          // skip validation if linked to other fitment guide
          if (context.document?.link) return true

          // unique fitment type validation
          const uniqueLookup = fitments?.reduce((a: Record<string, number>, e: any) => {
            // count how many times each fitment type is used
            a[e?.fitmentType?._ref] = ++a[e?.fitmentType?._ref] || 0
            return a
          }, {} as Record<string, number>)

          const duplicates = fitments?.reduce((map: any[], fitment: any) => {
            // check if fitment type is used more than once
            if (uniqueLookup[fitment?.fitmentType?._ref]) {
              map.push({ _key: fitment._key })
            }
            return map
          }, [])

          if (duplicates?.length && duplicates.length > 0) {
            return {
              message: 'Fitments must have unique fitment type',
              paths: duplicates,
            }
          }

          // every validation passed
          return true
        }),
      of: [
        {
          name: 'item',
          type: 'object',
          fields: [
            {
              name: 'fitmentType',
              title: 'Fitment',
              type: 'reference',
              to: [{ type: 'fitment' }],
              validation: (Rule) => Rule.required(),
              options: {
                // filter out fitment types already used in other fitments
                filter: ({ document }: { document: any }) => {
                  const fitmentTypes = document?.fitments?.map(
                    (fitment: any) => fitment.fitmentType?._ref,
                  )
                  if (!fitmentTypes) return true
                  return {
                    filter: `!(_id in $fitmentTypes)`,
                    params: {
                      fitmentTypes,
                    },
                  }
                },
              },
            },
            {
              title: 'Page blocks',
              name: 'pageBlocks',
              type: 'array',
              of: [...PageBlockTypes],
            },
          ],
          preview: {
            select: {
              fitment: 'fitmentType.name',
            },
            prepare({ fitment = 'Untitled' }) {
              return {
                title: fitment,
                subtitle: 'Fitment type',
              }
            },
          },
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'otherData',
      title: 'Other Data',
      fieldset: 'fitmentData',
      type: 'object',
      fields: [
        {
          name: 'title',
          title: 'Title',
          type: 'string',
        },
        {
          title: 'Page blocks',
          name: 'pageBlocks',
          type: 'array',
          of: [...PageBlockTypes],
        },
      ],
      group: 'content',
    }),
    // Keep the simplified vehicle field for backward compatibility
    defineField({
      name: 'vehicle',
      title: 'Vehicle',
      type: 'object',
      group: 'content',
      description: 'Optional vehicle information for organization',
      options: {
        collapsible: true,
        collapsed: true,
      },
      fields: [
        {
          name: 'make',
          title: 'Make',
          type: 'reference',
          to: [{ type: 'make' }],
        },
        {
          name: 'model',
          title: 'Model',
          type: 'reference',
          to: [{ type: 'model' }],
        },
      ],
    }),
  ],
  preview: {
    select: {
      title: 'pageInfo.title',
      lastEdited: '_updatedAt',
      fitments: 'fitments',
      isEnabled: 'isEnabled',
      link: 'link',
      connectedFitmentGuide: 'connectedFitmentGuide',
      connectedFitmentGuideEnabled: 'connectedFitmentGuide.isEnabled',
      connectedFitmentGuideFitments: 'connectedFitmentGuide.fitments',
      slug: 'pageInfo.canonical.current',
      make: 'vehicle.make.name',
      model: 'vehicle.model.name',
    },
    prepare({
      title = 'New page',
      lastEdited,
      link,
      fitments,
      isEnabled,
      connectedFitmentGuide,
      connectedFitmentGuideEnabled,
      connectedFitmentGuideFitments,
      slug,
      make,
      model,
    }) {
      const formatedDate = new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }).format(new Date(lastEdited))

      let subtitle = `Last edited: ${formatedDate}`
      if (make && model) {
        subtitle = `${make} ${model} • ${subtitle}`
      }

      let errorSubtitle
      let warningSubtitle

      if (!link && !fitments?.length) warningSubtitle = 'Warning (No fitments)'
      if (link && !connectedFitmentGuideFitments?.length)
        warningSubtitle = 'Warning (No fitments On Linked Fitment Guide)'

      if (link && !connectedFitmentGuide) errorSubtitle = 'Linked Fitment Guide Not Defined'
      if (link && !connectedFitmentGuideEnabled) errorSubtitle = 'Linked Fitment Guide Disabled'
      if (!link && !isEnabled) errorSubtitle = 'Disabled'
      if (!slug) errorSubtitle = 'Invalid (Missing slug)'

      return {
        title: title,
        subtitle: errorSubtitle || warningSubtitle || subtitle,
        media: errorSubtitle ? '🚫' : warningSubtitle ? '🔥' : '✅',
      }
    },
  },
})
